import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import focoLogo from '@/assets/foco-ai-logo.png';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Email inválido', description: 'Por favor ingresa un email válido.', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setEmailSent(true);
      toast({ title: 'Solicitud recibida', description: 'Si el correo está registrado, recibirás instrucciones en los próximos minutos.' });
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      toast({ title: 'Error', description: 'Hubo un problema al enviar el correo. Intenta de nuevo.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-8">
      <div className="w-full max-w-md space-y-6 sm:space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
            <img src={focoLogo} alt="FOCO AI System" className="h-14 sm:h-16 w-auto" />
          </div>
          
          {emailSent ? (
            <>
              <div className="mx-auto w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-accent" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">Revisa tu email</h2>
              <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                Hemos enviado un enlace de recuperación a <strong>{email}</strong>. 
                Haz clic en el enlace para restablecer tu contraseña.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl sm:text-3xl font-bold">¿Olvidaste tu contraseña?</h2>
              <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>
            </>
          )}
        </div>

        {!emailSent && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium leading-none">Email</label>
              <Input id="email" type="email" inputMode="email" autoComplete="email"
                autoCapitalize="none" autoCorrect="off" placeholder="tu@email.com" className="h-12 text-base"
                value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full h-12 text-base touch-manipulation" disabled={isLoading}>
              {isLoading ? 'Enviando...' : 'Enviar enlace de recuperación'}
            </Button>
          </form>
        )}

        <div className="text-center">
          <Link to="/auth">
            <Button variant="link" className="text-muted-foreground h-12 touch-manipulation">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a iniciar sesión
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
